import { Plus } from 'lucide-react';
import { Fragment } from 'react';

import { Button } from '@/components/ui/Button';
import type { Slide as SlideType } from '@/lib/schema.types';
import { cn } from '@/lib/utils';
import { useEditorStore, useScript } from '@/store/editor.store';

import { useRequestBlankSlide } from './BlankSlideInsertContext';
import { QuizSetGroup, QuizSetSlideHeader } from './QuizSetGroup';
import { Slide } from './Slide';
import { SlideSeparator } from './SlideSeparator';

export function CanvasContainer() {
  const script = useScript();
  const mode = useEditorStore((s) => s.mode);
  const { requestNewSlide } = useRequestBlankSlide();

  if (!script) return null;

  // Quiz-set grouping only applies in pack mode; scripts render as before.
  const blocks =
    mode === 'pack' ? groupSlides(script.slides) : asSingletons(script.slides);

  return (
    <div
      className={cn(
        'relative flex-1 overflow-y-auto bg-canvas scroll-thin',
        // Pack-mode-only — sets Kalam as the cascade default for every text
        // element on a slide (Kalam covers Latin + Devanagari; Inter /
        // system-ui fall back for any glyph it lacks). Per-element
        // `style.fontFamily` still overrides this naturally.
        mode === 'pack' && 'canvas-pack-font',
      )}
    >
      <div className="mx-auto flex w-full max-w-[1200px] flex-col gap-8 px-8 py-12">
        {blocks.map((block, blockIdx) => {
          if (block.kind === 'slide') {
            const { slide, index, total } = block;
            return (
              <Fragment key={slide.id}>
                <Slide slide={slide} index={index} total={total} />
                <SlideSeparator insertAtIndex={index + 1} />
              </Fragment>
            );
          }

          // Quiz-set block — wrap the run of consecutive quiz slides in the
          // amber-rail group. Suppress per-slide separators *inside* the run
          // (the "Add another question" button replaces them); keep one
          // SlideSeparator after the group to allow inserting a non-quiz
          // slide between this set and the next block.
          const lastSlideInBlock = block.slides[block.slides.length - 1];
          return (
            <Fragment key={`quizset-${block.setIndex}`}>
              <QuizSetGroup
                setIndex={block.setIndex}
                lastSlideId={lastSlideInBlock.slide.id}
                total={block.slides.length}
              >
                {block.slides.map(({ slide, index, total, positionInSet }) => (
                  <Fragment key={slide.id}>
                    <QuizSetSlideHeader
                      position={positionInSet}
                      total={block.slides.length}
                    />
                    <Slide slide={slide} index={index} total={total} />
                  </Fragment>
                ))}
              </QuizSetGroup>
              <SlideSeparator
                insertAtIndex={
                  lastSlideInBlock.index + 1
                }
                /* key is implicit via Fragment + blockIdx, kept for clarity */
                key={`sep-after-${blockIdx}`}
              />
            </Fragment>
          );
        })}
        <div className="-mt-2 flex items-center justify-center">
          <Button variant="secondary" onClick={() => requestNewSlide()}>
            <Plus size={14} />
            Add slide
          </Button>
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Grouping                                                                    */
/* -------------------------------------------------------------------------- */

type SlideRef = { slide: SlideType; index: number; total: number };

type RenderBlock =
  | ({ kind: 'slide' } & SlideRef)
  | {
      kind: 'quizSet';
      setIndex: number;
      slides: (SlideRef & { positionInSet: number })[];
    };

/** Walk slides[] and collapse consecutive `kind:"quiz"` runs into a single
 *  `quizSet` block. Non-quiz slides remain as standalone `slide` blocks. */
function groupSlides(slides: SlideType[]): RenderBlock[] {
  const out: RenderBlock[] = [];
  const total = slides.length;
  let setCounter = 0;
  let i = 0;
  while (i < total) {
    const s = slides[i];
    if (s.beatMeta?.kind === 'quiz') {
      const run: (SlideRef & { positionInSet: number })[] = [];
      while (i < total && slides[i].beatMeta?.kind === 'quiz') {
        run.push({
          slide: slides[i],
          index: i,
          total,
          positionInSet: run.length + 1,
        });
        i += 1;
      }
      setCounter += 1;
      out.push({ kind: 'quizSet', setIndex: setCounter, slides: run });
      continue;
    }
    out.push({ kind: 'slide', slide: s, index: i, total });
    i += 1;
  }
  return out;
}

/** Script-mode passthrough: every slide its own block. */
function asSingletons(slides: SlideType[]): RenderBlock[] {
  return slides.map((slide, i) => ({
    kind: 'slide',
    slide,
    index: i,
    total: slides.length,
  }));
}
